// Cinematic native launch animation for FitAura AI.
// Plays once per session on native (Android/iOS) only — web users skip it.
// Mounted above <App /> in main.tsx so the real app is already rendering underneath.
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { isNativeApp } from "@/lib/platform";
import logoSrc from "/app-icon-1024.png";

const SESSION_KEY = "fitaura.brandIntro.played";
const TOTAL_DURATION_MS = 2150; // 1800ms reveal + 350ms fade-out

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export default function BrandIntro() {
  // Decide synchronously on first render whether to show — avoids any flash.
  const [show, setShow] = useState(() => {
    if (typeof window === "undefined") return false;
    if (!isNativeApp()) return false;
    if (prefersReducedMotion()) return false;
    if (sessionStorage.getItem(SESSION_KEY) === "1") return false;
    return true;
  });

  useEffect(() => {
    if (!show) return;
    sessionStorage.setItem(SESSION_KEY, "1");
    const t = window.setTimeout(() => setShow(false), TOTAL_DURATION_MS);
    return () => window.clearTimeout(t);
  }, [show]);

  // Particle positions — generated once.
  const particles = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.6,
        duration: 1.6 + Math.random() * 0.8,
        size: 3 + Math.random() * 4,
        hue: Math.random() > 0.5 ? "258 78% 66%" : "187 85% 58%",
      })),
    [],
  );

  const word = "FitAura";

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="brand-intro"
          // Tap-to-dismiss escape hatch
          onClick={() => setShow(false)}
          initial={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.08 }}
          transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
          className="fixed inset-0 z-[9999] overflow-hidden bg-[#0F172A]"
          style={{
            // Disable pointer events near the end so taps fall through early.
            pointerEvents: "auto",
          }}
        >
          {/* Stage 2a — Radial bloom (violet → cyan) */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1.5, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(circle at 50% 50%, hsl(258 78% 56% / 0.55) 0%, hsl(187 85% 38% / 0.35) 35%, transparent 65%)",
            }}
          />

          {/* Stage 2b — Drifting particles */}
          <div className="absolute inset-0 overflow-hidden">
            {particles.map((p) => (
              <motion.span
                key={p.id}
                initial={{ y: "110vh", opacity: 0 }}
                animate={{ y: "-10vh", opacity: [0, 0.8, 0] }}
                transition={{
                  duration: p.duration,
                  delay: p.delay,
                  ease: "easeOut",
                }}
                className="absolute rounded-full blur-[1px]"
                style={{
                  left: `${p.left}%`,
                  width: p.size,
                  height: p.size,
                  background: `hsl(${p.hue})`,
                  boxShadow: `0 0 12px hsl(${p.hue} / 0.8)`,
                }}
              />
            ))}
          </div>

          {/* Center stack */}
          <div className="relative z-10 flex h-full w-full flex-col items-center justify-center gap-6 px-6">
            {/* Logo + aura ring */}
            <div className="relative flex items-center justify-center">
              {/* Aura ring pulse */}
              <motion.span
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1.4, opacity: [0, 0.6, 0] }}
                transition={{ duration: 1.1, delay: 0.4, ease: "easeOut" }}
                className="absolute h-32 w-32 rounded-full border-2"
                style={{
                  borderColor: "hsl(258 78% 66% / 0.7)",
                  boxShadow:
                    "0 0 30px hsl(258 78% 66% / 0.5), inset 0 0 20px hsl(187 85% 58% / 0.3)",
                }}
              />
              {/* Logo mark */}
              <motion.img
                src={logoSrc}
                alt="FitAura AI"
                initial={{ scale: 0, opacity: 0, rotate: -8 }}
                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                transition={{
                  delay: 0.2,
                  type: "spring",
                  damping: 12,
                  stiffness: 180,
                }}
                className="relative h-24 w-24 rounded-3xl shadow-2xl"
                draggable={false}
              />
            </div>

            {/* Wordmark — "FitAura" letter-by-letter + "AI" badge */}
            <div className="flex items-center gap-2">
              <div className="flex">
                {word.split("").map((char, i) => (
                  <motion.span
                    key={i}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      delay: 0.6 + i * 0.03,
                      duration: 0.4,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    className="text-4xl font-bold tracking-tight text-white"
                    style={{
                      textShadow: "0 0 24px hsl(258 78% 66% / 0.4)",
                    }}
                  >
                    {char}
                  </motion.span>
                ))}
              </div>
              <motion.span
                initial={{ opacity: 0, x: 24, filter: "blur(8px)" }}
                animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                transition={{ delay: 1.1, duration: 0.5, ease: "easeOut" }}
                className="rounded-lg px-2 py-0.5 text-2xl font-bold tracking-tight"
                style={{
                  background:
                    "linear-gradient(135deg, hsl(258 78% 56%), hsl(187 85% 48%))",
                  color: "white",
                  boxShadow:
                    "0 0 24px hsl(258 78% 56% / 0.6), 0 0 48px hsl(187 85% 48% / 0.3)",
                }}
              >
                AI
              </motion.span>
            </div>

            {/* Tagline */}
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.5, ease: "easeOut" }}
              className="text-sm font-medium tracking-wide text-white/70"
            >
              Your style, reimagined.
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
